import React, { useState } from 'react';
import axios, { getErrorMessage } from '../../api/axios';

// ADDED: shrinks a picked photo before it is sent. The images land in Postgres as Bytes,
// so a full 4MB phone photo would bloat the database and every later page load.
const MAX_IMAGE_EDGE = 900;

// MODIFIED: exported so the multipack form can reuse the same resize step
export function fileToResizedDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Could not read that file'));
        reader.onload = () => {
            const image = new Image();
            image.onerror = () => reject(new Error('That file is not a valid image'));
            image.onload = () => {
                const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.width, image.height));
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(image.width * scale);
                canvas.height = Math.round(image.height * scale);
                canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            image.src = reader.result;
        };
        reader.readAsDataURL(file);
    });
}

// ADDED: client-side validation. The server already rejects bad data, but it only
// reports the first problem and only after a round trip - these rules point at the exact
// field while the user is still looking at it.
const CURRENT_YEAR = new Date().getFullYear();
const SCALE_PATTERN = /^\d{1,2}\s*:\s*\d{1,4}$/; // 1:64, 1:18, ...

const VALIDATORS = {
    nameOnPackage: (value) => {
        if (!value.trim()) return 'Name is required.';
        if (value.trim().length < 2) return 'Name is too short.';
        if (value.length > 80) return 'Name must be 80 characters or fewer.';
        return '';
    },
    seriesId: (value) => {
        if (!value.trim()) return 'Series code is required.';
        if (value.trim().length > 32) return 'Series code must be 32 characters or fewer.';
        return '';
    },
    make: (value) => (value.trim() ? (value.length > 60 ? 'Make must be 60 characters or fewer.' : '') : 'Make is required.'),
    model: (value) => (value.trim() ? (value.length > 60 ? 'Model must be 60 characters or fewer.' : '') : 'Model is required.'),
    series: (value) => (value.trim() ? (value.length > 80 ? 'Package series must be 80 characters or fewer.' : '') : 'Package series is required.'),
    color: (value) => (value.trim() ? (value.length > 40 ? 'Colour must be 40 characters or fewer.' : '') : 'Colour is required.'),
    modelScale: (value) => {
        if (!value.trim()) return 'Scale is required.';
        if (!SCALE_PATTERN.test(value.trim())) return 'Use a scale like 1:64.';
        return '';
    },
    yearReleased: (value) => {
        if (!String(value).trim()) return 'Year is required.';
        const year = Number(value);
        if (!Number.isInteger(year)) return 'Year must be a whole number.';
        // Hot Wheels began in 1968; next year allows for upcoming releases
        if (year < 1968 || year > CURRENT_YEAR + 1) return `Year must be between 1968 and ${CURRENT_YEAR + 1}.`;
        return '';
    },
    // MODIFIED: price is optional. A blank field saves as "not priced yet" rather than
    // as zero, so an unpriced model is not confused with a worthless one.
    price: (value) => {
        if (!String(value).trim()) return '';
        const price = Number(value);
        if (Number.isNaN(price)) return 'Price must be a number.';
        if (price < 0) return 'Price cannot be negative.';
        if (price > 100000) return 'That price looks wrong.';
        if (!/^\d*(\.\d{1,2})?$/.test(String(value).trim())) return 'Use at most two decimal places.';
        return '';
    },
};

// MODIFIED: onAddExisting opens the "add to a collection" modal for a model that turned out
// to be in the catalog already
export default function NewCarForm({ isVisible, onClose, onCreated, onAddExisting }) {

    // Input fields data from the user
    const [frontImage, setFrontImage] = useState(null);
    const [backImage, setBackImage] = useState(null);
    const [nameOnPackage, setNameOnPackage] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [series, setSeries] = useState('');
    // ADDED: the remaining columns the schema requires. Without these the insert
    // could never succeed, since they are all NOT NULL.
    const [seriesId, setSeriesId] = useState('');
    const [color, setColor] = useState('');
    const [modelScale, setModelScale] = useState('1:64');
    const [yearReleased, setYearReleased] = useState('');
    const [price, setPrice] = useState('');

    const [errorMessage, setErrorMessage] = useState('');
    // ADDED: the catalog entry this form matched, when the server refuses a duplicate
    const [duplicate, setDuplicate] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    // ADDED: per-field messages, keyed by the same ids the fields array uses
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fields for the form
    const fields = [
        { id: 'name', key: 'nameOnPackage', label: 'Name', placeholder: 'Enter name on the package', value: nameOnPackage, onChange: setNameOnPackage },
        // MODIFIED: codes are printed in capitals on the package, so the field keeps them that way
        { id: 'seriesId', key: 'seriesId', label: 'Series code', placeholder: 'Code on the package (Ex.: HTD01)', value: seriesId, onChange: (value) => setSeriesId(value.toUpperCase()), caps: true },
        { id: 'make', key: 'make', label: 'Make', placeholder: 'Enter make', value: make, onChange: setMake },
        { id: 'model', key: 'model', label: 'Model', placeholder: 'Enter model', value: model, onChange: setModel },
        { id: 'series', key: 'series', label: 'Package series', placeholder: 'Ex.: HW Celebration Racers', value: series, onChange: setSeries },
        { id: 'color', key: 'color', label: 'Colour', placeholder: 'Enter colour', value: color, onChange: setColor },
        { id: 'modelScale', key: 'modelScale', label: 'Scale', placeholder: 'Ex.: 1:64', value: modelScale, onChange: setModelScale },
        { id: 'datereleased', key: 'yearReleased', label: 'Year released', placeholder: 'Ex.: 2024', value: yearReleased, onChange: setYearReleased, type: 'number' },
        { id: 'price', key: 'price', label: 'Price (optional)', placeholder: 'Ex.: 1.25', value: price, onChange: setPrice, type: 'number', optional: true }, // MODIFIED
    ];

    // MODIFIED: these used to keep a blob: URL that was never uploaded anywhere.
    // They now hold a resized base64 data URL, which is what the API stores.
    const handleImageChange = async (e, setImage) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setErrorMessage('');
            setImage(await fileToResizedDataUrl(file));
        } catch (error) {
            setErrorMessage(error.message);
        }
    };

    // ADDED: one picker for both photos. Most packages get photographed front then back in
    // the same burst, so selecting both at once and letting the first fill the front slot
    // matches how the photos already sit on the phone.
    //
    // Only the first two files are used - picking a whole camera roll should not silently
    // throw most of it away without saying so.
    const handleBothImages = async (event) => {
        const files = Array.from(event.target.files || []).slice(0, 2);
        if (files.length === 0) return;

        try {
            setErrorMessage('');
            const [front, back] = await Promise.all(files.map(fileToResizedDataUrl));
            setFrontImage(front);
            if (back) setBackImage(back);
        } catch (error) {
            setErrorMessage(error.message);
        }
        // Clear the input so picking the same two files again still fires onChange
        event.target.value = '';
    };

    // ADDED: the file picker does not promise an order, and phones often hand them back
    // the other way round. One tap fixes it rather than reselecting both files.
    const swapImages = () => {
        setFrontImage(backImage);
        setBackImage(frontImage);
    };

    // ADDED: runs every rule and returns the error map. Empty map means the form is valid.
    const validateAll = () => {
        const values = {
            nameOnPackage, seriesId, make, model, series, color, modelScale, yearReleased, price,
        };
        return Object.entries(VALIDATORS).reduce((errors, [key, validate]) => {
            const message = validate(values[key] ?? '');
            if (message) errors[key] = message;
            return errors;
        }, {});
    };

    // Re-check one field as soon as the user leaves it
    const handleBlur = (key, value) => {
        setTouched((previous) => ({ ...previous, [key]: true }));
        setFieldErrors((previous) => ({ ...previous, [key]: VALIDATORS[key] ? VALIDATORS[key](value ?? '') : '' }));
    };

    // ADDED: clear the form so the modal is empty next time it opens
    const resetForm = () => {
        setFieldErrors({});
        setTouched({});
        setFrontImage(null);
        setBackImage(null);
        setNameOnPackage('');
        setMake('');
        setModel('');
        setSeries('');
        setSeriesId('');
        setColor('');
        setModelScale('1:64');
        setYearReleased('');
        setPrice('');
    };

    // Create a new car in DB
    const newCar = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        setDuplicate(null); // ADDED

        // MODIFIED: the payload now matches the schema. It used to send a "nameonpackage"
        // key that does not exist and omit every required column.
        // ADDED: stop here if anything is invalid, and show every problem at once rather
        // than letting the server reject them one at a time.
        const errors = validateAll();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setTouched(Object.keys(VALIDATORS).reduce((all, key) => ({ ...all, [key]: true }), {}));
            setErrorMessage('Please fix the highlighted fields.');
            return;
        }

        // MODIFIED: values are trimmed, so a stray space cannot create a duplicate
        // catalog entry that looks identical to an existing one.
        const payload = {
            name: nameOnPackage.trim(),
            seriesId: seriesId.trim(),
            brand: make.trim(),
            model: model.trim(),
            hwcollectionName: series.trim(),
            color: color.trim(),
            modelScale: modelScale.trim(),
            datereleased: Number(yearReleased),
            // MODIFIED: blank means "not priced yet", which the API stores as null
            price: price.trim() === '' ? null : Number(price),
            imageFront: frontImage,
            imageBack: backImage,
        };

        setIsSubmitting(true);
        try {
            // MODIFIED: posts to /cars. The old '/NewCar' endpoint did not exist,
            // and the old `response.ok` check is a fetch idiom that axios never sets.
            const response = await axios.post('/cars', payload, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            setSuccessMessage(response.data.created
                ? 'Model added to the catalog.'
                : 'That model was already in the catalog.');
            resetForm();
            if (onCreated) onCreated(); // let the Models grid refresh
        } catch (error) {
            // ADDED: 409 means this model is already in the catalog. Nothing is cleared -
            // the typed fields and both photos stay, in case the match is not what they meant.
            if (error.response?.status === 409 && error.response.data?.existing) {
                setDuplicate(error.response.data.existing);
            } else {
                setErrorMessage(getErrorMessage(error, 'Could not add the model.'));
            }
            console.error('Error adding car:', error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClose = () => {
        setErrorMessage('');
        setSuccessMessage('');
        setDuplicate(null); // ADDED
        onClose();
    };

    if (!isVisible) return null;

    // One upload box, used for both the front and back images
    // MODIFIED (styling): the well, border and hint text now come from the theme tokens
    const renderImagePicker = (image, setImage, label) => (
        <div className='flex flex-col items-center justify-center'>
            <div className="cv-well relative w-32 h-32 sm:w-40 sm:h-40 rounded-xl border-dashed flex items-center justify-center overflow-hidden transition duration-300 hover:border-brand-flame">
                {image ? (
                    <>
                        <img src={image} alt={label} className="w-full h-full object-contain" />
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <p className="text-white text-sm">Change image</p>
                        </div>
                        <input type="file" accept="image/*" aria-label={`Change ${label}`} className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleImageChange(e, setImage)} />
                    </>
                ) : (
                    <>
                        <input type="file" accept="image/*" aria-label={label} className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleImageChange(e, setImage)} />
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 cv-muted">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                    </>
                )}
            </div>
            <p className="text-xs cv-muted mt-2 uppercase tracking-wider">{label}</p>
        </div>
    );

    // MODIFIED (styling): shared card/button/input classes, and the modal keeps the
    // speed stripe the rest of the app uses.
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl cv-card relative max-h-[90vh] overflow-y-auto overflow-x-hidden animate-fade-up">
                <div className="cv-stripe" />
                <button onClick={handleClose} aria-label="Close"
                        className="absolute top-4 right-3 cv-muted hover:text-brand-flame z-10 transition duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
                        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
                    </svg>
                </button>
                {/* MODIFIED: a real onSubmit rather than an onClick on the button */}
                <form className="px-4 sm:px-6 pb-6 pt-6" onSubmit={newCar}>
                    <div className="text-center mb-5">
                        <p className="cv-eyebrow">New entry</p>
                        <h1 className="cv-display text-2xl sm:text-3xl">Add a model</h1>
                    </div>

                    {errorMessage && <p role="alert" className="cv-alert-error mb-4">{errorMessage}</p>}

                    {/* ADDED: shown instead of saving when the same Series code, Make and Model
                        already exist (capitals and spaces ignored). The form is left as it was. */}
                    {duplicate &&
                        <div role="alert" className="cv-alert-error mb-4 space-y-3">
                            <p className="font-semibold">This model is already in the catalog.</p>
                            <div className="flex items-center gap-3">
                                <div className="cv-well w-14 h-14 rounded-lg shrink-0 flex items-center justify-center overflow-hidden">
                                    {duplicate.imageThumb
                                        ? <img src={duplicate.imageThumb} alt={duplicate.name} className="w-full h-full object-contain" />
                                        : <span className="text-[0.6rem] cv-muted">No image</span>}
                                </div>
                                <div className="min-w-0 text-sm">
                                    <p className="cv-strong break-words">{duplicate.name}</p>
                                    <p className="cv-muted truncate">
                                        {duplicate.seriesId} &middot; {duplicate.brand} {duplicate.model} &middot; {duplicate.datereleased}
                                    </p>
                                </div>
                            </div>
                            {onAddExisting &&
                                <button type="button" onClick={() => onAddExisting(duplicate)}
                                        className="cv-btn cv-btn-primary w-full text-sm">
                                    Add the existing one to a collection
                                </button>
                            }
                        </div>
                    }
                    {successMessage && <p className="cv-alert-success mb-4">{successMessage}</p>}

                    {/* MODIFIED: both photos can now be picked in one go. The two boxes below
                        still work on their own, for replacing just one of them. */}
                    <div className="w-full rounded-xl flex flex-col items-center mb-5 gap-4 py-5 px-4"
                         style={{ backgroundColor: 'var(--surface-sunken)' }}>
                        <div className="flex flex-row items-center justify-center gap-3 sm:gap-5">
                            {renderImagePicker(frontImage, setFrontImage, 'Front image')}

                            {/* Only useful once there is something to swap */}
                            {(frontImage || backImage) &&
                                <button type="button" onClick={swapImages}
                                        title="Swap front and back"
                                        aria-label="Swap front and back images"
                                        className="cv-muted hover:text-brand-flame p-2 rounded-lg transition duration-300 self-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                                    </svg>
                                </button>
                            }

                            {renderImagePicker(backImage, setBackImage, 'Back image')}
                        </div>

                        <label className="cv-btn cv-btn-quiet text-sm cursor-pointer">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
                            </svg>
                            Pick both photos
                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleBothImages} />
                        </label>
                        <p className="text-xs cv-muted -mt-2 text-center">
                            First photo becomes the front, second the back. Use the arrows to swap them.
                        </p>
                    </div>

                    {/* Fields */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3'>
                        {fields.map((field) => (
                            <div key={field.id}>
                                <label className="cv-label" htmlFor={field.id}>
                                    {field.label}
                                </label>
                                <input
                                    id={field.id}
                                    type={field.type || 'text'}
                                    step={field.id === 'price' ? '0.01' : undefined}
                                    placeholder={field.placeholder}
                                    autoComplete='off'
                                    required={!field.optional}
                                    // ADDED: opens the phone keyboard in capitals for the code field
                                    autoCapitalize={field.caps ? 'characters' : undefined}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        // Once a field has been flagged, re-check as they type so the
                                        // message clears the moment it is fixed
                                        if (touched[field.key]) {
                                            setFieldErrors((previous) => ({ ...previous, [field.key]: VALIDATORS[field.key](e.target.value) }));
                                        }
                                    }}
                                    onBlur={(e) => handleBlur(field.key, e.target.value)}
                                    aria-invalid={Boolean(fieldErrors[field.key])}
                                    aria-describedby={fieldErrors[field.key] ? `${field.id}-error` : undefined}
                                    className="cv-input"
                                    style={fieldErrors[field.key] ? { borderColor: 'var(--accent)' } : undefined}
                                />
                                {fieldErrors[field.key] &&
                                    <p id={`${field.id}-error`} className="text-xs cv-accent mt-1">{fieldErrors[field.key]}</p>
                                }
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        <button type="submit" disabled={isSubmitting} className="cv-btn cv-btn-primary flex-1 py-3 uppercase">
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={handleClose} className="cv-btn cv-btn-quiet flex-1 py-3">
                            Close
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
